import { z } from "zod";

/**
 * The draft's subject is not a surface of the reply (`docs/2026-10-05-draft-subject-never-sent.md`).
 *
 * A reply goes out under its thread's own subject: the provider's reply endpoint takes none and sets
 * the real one. So the writer is not asked for a subject, the host stores the thread's reply subject
 * as the draft's, the judges are not shown it, and the rules read the body alone. Pure, so the
 * browser imports `replySubject` through the reply box.
 */

/** The subject a reply carries: the turn's own, once prefixed. Bison sets the real one. */
export function replySubject(subject: string): string {
  const line = subject.split(/\r?\n/u)[0]?.trim() ?? "";
  if (!line) return "";
  return /^re:/iu.test(line) ? line : `Re: ${line}`;
}

/** A thread with no subject anywhere still has a reply subject: the bare prefix. */
const NO_SUBJECT = "Re:";

/**
 * The subject stored with a drafted reply, as the reply box shows it (`rep-reply-store.ts`): the
 * message being answered, else the newest message in the thread that has one.
 */
export function threadReplySubject(thread: {
  readonly trigger_message_id: string;
  readonly messages: readonly { readonly id: string; readonly subject: string | null }[];
}): string {
  const subjects = [
    thread.messages.find((message) => message.id === thread.trigger_message_id)?.subject,
    ...thread.messages.map((message) => message.subject).reverse(),
  ];
  const own = subjects.find((subject) => subject?.trim());
  return replySubject(own ?? "") || NO_SUBJECT;
}

const Shown = z.record(z.string(), z.unknown());
function withoutSubject(value: unknown): unknown {
  const parsed = Shown.safeParse(value);
  if (!parsed.success || !("subject" in parsed.data)) return value;
  const { subject: _subject, ...rest } = parsed.data;
  return rest;
}

/**
 * The stage data a model is shown: a judge's candidate and the writer's previous draft without the
 * subject the host set, so neither is asked to assess or revise a line nobody sends. Order is kept.
 */
export function shownWithoutDraftSubject(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(payload).map(([key, value]) =>
      key === "draft" || key === "previous_draft" ? [key, withoutSubject(value)] : [key, value],
    ),
  );
}
